// import { showAlertToasterByStatusMessage } from '../components/EpAlertToaster';
import i18next from 'i18next';
import {
  USER_GET_APPS_ENDPOINT,
  USER_GET_HOSPITAL_CLINICAL_PHARMACY_INFO_ENDPOINT,
  USER_GET_HOSPITAL_INFO_ENDPOINT,
  USER_GET_PERMISSIONS_ENDPOINT,
  USER_GET_TENANTS_ENDPOINT,
} from '../constants/userConstants';
import { IServiceQueryProps } from '../interfaces';
import { useHospitalStore } from '../stores';
import { apiService } from '../utils';
import { httpServices } from './httpServices';
import { signinSilent } from './authService';
import { useSearchParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { defaultBundlesId, generateBundleIdsQueries } from '../utils/bundle';

const { get, getNoCache, put } = httpServices;
const SECURITY_BASE_API_URL = process.env.NEXT_PUBLIC_BASE_API_URL_SECURITY;
// const ADMISSIONS_BASE_API_URL = process.env.NEXT_PUBLIC_BASE_API_URL_ADMISSIONS;

const userGetServices = ({
  key,
  url,
  params,
  enabled,
  errorCallbackFunc,
  retry,
}: IServiceQueryProps) =>
  get({ key, url: url, params, enabled, errorCallbackFunc, retry });

const getHospitalInfo = (hospitalIdSp: string, enabled?: boolean) =>
  userGetServices({
    key: ['hospitalContact', i18next.language, hospitalIdSp],
    url: apiService() + USER_GET_HOSPITAL_INFO_ENDPOINT,
    enabled,
    params: {
      CultureCode: i18next.language,
      HospitalId: hospitalIdSp,
    },
  });

const getHospitalClinicalPharmacyInfo = (
  hospitalIdSp: string,
  enabled?: boolean,
) => {
  const isPublicPath =
    typeof window === 'undefined'
      ? false
      : window.location.pathname.includes('/Public');
  const api = process.env.NEXT_PUBLIC_BASE_API_URL_SP;
  const publicApi = api + 'user-not-logged/clinical-pharmacies/contacts';
  const privateApi = api + USER_GET_HOSPITAL_CLINICAL_PHARMACY_INFO_ENDPOINT;

  return userGetServices({
    key: [
      'hospitalContact',
      'clinical-pharmacy',
      i18next.language,
      hospitalIdSp,
    ],
    url: isPublicPath ? publicApi : privateApi,
    enabled,
    params: {
      CultureCode: i18next.language,
      HospitalId: hospitalIdSp,
    },
  });
};

const GetHospitalLogo = (structureId: string, enabled?: boolean) => {
  return userGetServices({
    key: ['hospitalLogos', i18next.language],
    url:
      apiService(SECURITY_BASE_API_URL) + 'hospitals/' + structureId + '/logos',
    enabled,
    retry: false,
    errorCallbackFunc: (error) => {
      if (error === 401) {
        signinSilent();
      }
    },
  });
};

const getHospitalLogoNotLogged = (structureId: string, enabled?: boolean) => {
  return userGetServices({
    key: ['hospitalLogos', 'not-logged', i18next.language],
    url: SECURITY_BASE_API_URL + 'hospitals/' + structureId + '/logos',
    enabled,
    retry: false,
  });
};

const GetTenantsUnits = ({
  Enabled,
  Monitored,
}: {
  Enabled?: boolean;
  Monitored?: string;
}) => {
  //const { HospitalId, NetworkId, TenantId } = useHospitalStore(state => state).hospitalInfo;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const hospitalInfo = useHospitalStore((state) => state).hospitalInfo;
  const searchParams = useSearchParams();
  const hospitalId =
    searchParams.get('HospitalId') || searchParams.get('hospitalId');

  if (!hospitalInfo) {
    // Tratar o caso em que hospitalInfo é undefined, se necessário
    return null; // Ou retornar um valor padrão apropriado
  }
  const { HospitalId } = hospitalInfo;

  return userGetServices({
    key: [
      'tenantsUnits',
      i18next.language,
      (HospitalId as string) || (hospitalId as string),
      // NetworkId,
      // TenantId,
      Monitored as string,
    ],
    url: apiService() + USER_GET_TENANTS_ENDPOINT + 'networks/hospitals/units',
    params: {
      // TenantId,
      // NetworkId,
      HospitalId: HospitalId || hospitalId,
      ItensPerPage: 300,
      ...(Enabled && { Enabled }),
      ...(Monitored && { Monitored }),
    },
  });
};

const getUserPermissions = ({
  enabled,
  bundlesIds,
}: {
  enabled: boolean;
  bundlesIds?: string[];
}) => {
  const bundlesQueries = generateBundleIdsQueries(
    (bundlesIds as string[]) || defaultBundlesId,
  );

  const bundleURL = `?LowestStructureLevel=4&${bundlesQueries}&CultureCode=${i18next.language}`;
  return getNoCache({
    key: ['userPermissions', i18next.language],
    url: SECURITY_BASE_API_URL + USER_GET_PERMISSIONS_ENDPOINT + bundleURL,
    enabled,
    errorCallbackFunc: (error) => {
      if (error === 401) {
        signinSilent();
      }
    },
  });
};
const getUserBundles = ({
  hospitalId,
  enabled,
  key,
}: {
  key: string[];
  enabled: boolean;
  hospitalId: string;
}) => {
  const isPublicPath =
    typeof window === 'undefined'
      ? false
      : window.location.pathname.includes('/Public');
  const privateApi = `${SECURITY_BASE_API_URL}hospitals/${hospitalId}/bundles/`;
  const publicApi = privateApi + 'user-not-logged';

  return get({
    key,
    url: isPublicPath ? publicApi : privateApi,
    enabled,
  });
};

const getUserApps = () => {
  return userGetServices({
    key: ['userApps', i18next.language],
    url: SECURITY_BASE_API_URL + USER_GET_APPS_ENDPOINT,
  });
};

const GetUserCulture = () => {
  const queryClient = useQueryClient();
  const isPublicPath =
    typeof window === 'undefined'
      ? false
      : window.location.pathname.includes('/Public');

  return get({
    key: ['user', 'culture'],
    url: SECURITY_BASE_API_URL + 'users/logged/default-culture',
    enabled: !isPublicPath && !queryClient.getQueryData(['user', 'culture']),
    retry: true,
  });
};
const PutUserCulture = () =>
  put({
    url: SECURITY_BASE_API_URL + 'users/logged/default-culture',
    data: {},
  });

export const userService = {
  getHospitalInfo,
  getHospitalClinicalPharmacyInfo,
  getHospitalLogo: GetHospitalLogo,
  getHospitalLogoNotLogged,
  getTenantsUnits: GetTenantsUnits,
  getUserPermissions,
  getUserApps,
  getUserBundles,
  getUserCulture: GetUserCulture,
  putUserCulture: PutUserCulture,
};
